import React from 'react';
import {
    Code as CodeIcon,
    Devices as IotIcon,
    Language as WebIcon,
    PhoneAndroid as MobileIcon,
    Analytics as AnalyticsIcon,
    Store as EcommerceIcon,
    School as EducationIcon,
    LocalHospital as HealthcareIcon,
    Theaters as MediaIcon,
    LocalShipping as LogisticsIcon,
    AttachMoney as FinanceIcon,
    Home as RealEstateIcon,
    Campaign as MarketingIcon,
    Assessment as EnterpriseIcon,
    RocketLaunch as MvpIcon,
    Cloud as SaasIcon,
    Inventory as ProductIcon,
    VerifiedUser as QualityIcon,
    Groups as TeamIcon,
    GppGood as RiskIcon,
    Update as AgileIcon,
    MonetizationOn as BudgetIcon,
    Schedule as DeadlineIcon,
    Description as ReportIcon
} from '@mui/icons-material';
import {
    Box,
    Button,
    Card,
    CardContent,
    Container,
    Divider,
    Grid,
    List,
    ListItem,
    ListItemIcon,
    Stack,
    Typography,
    useTheme,
    alpha,
    useMediaQuery,
    Chip
} from '@mui/material';
import { motion } from 'framer-motion';
import LanIcon from '@mui/icons-material/Lan';
import { useNavigate } from 'react-router-dom';

const AnimatedCard = motion(Card);
const AnimatedGrid = motion(Grid);

const ServicesPage = () => {

    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const mainServices = [
        {
            icon: <CodeIcon fontSize="large" />,
            title: "Custom software development",
            description: "We develop and deliver custom solutions of varying complexity for both startup and enterprise Clients. Our custom approach means we delve deeply into the Client's needs and business goals, shaping the concept or vision of the software solution and bringing it to life.",
            link: "Custom software development",
            to: '/software-development'
        },
        {
            icon: <IotIcon fontSize="large" />,
            title: "IoT development",
            description: "We develop IoT systems of any scale, from fleets of a few devices to hundreds. IoT development is our specialization, so we have enough expertise to provide consulting services along with development services.",
            link: "IoT development services",
            to: '/software-development'
        },
        {
            icon: <WebIcon fontSize="large" />,
            title: "Web development",
            description: "We develop scalable, high-performance, and secure web applications. While different web solutions may seem similar at first glance, we approach each one uniquely.",
            link: "Web development services",
            to: '/web-development'
        },
        {
            icon: <MobileIcon fontSize="large" />,
            title: "Mobile app development",
            description: "Our team of skilled mobile developers and creative designers build excellent iOS, Android, and cross-platform applications, crafting simple and intuitive interfaces.",
            link: "Mobile app development",
            to: '/mobile-app-development'
        },
        {
            icon: <AnalyticsIcon fontSize="large" />,
            title: "Digital marketing",
            description: "Boost your marketing with AI-powered automation! Our tools optimize campaigns, uncover actionable insights, and drive results—helping you reach the right audience, maximize ROI, and achieve your business goals faster. Smarter marketing starts here.",
            link: "Digital marketing services",
            to:'/digital-marketing'
        },
        {
            icon: <EcommerceIcon fontSize="large" />,
            title: "eCommerce development",
            description: "We build stronger connections with customers through web and mobile applications with superior shopping experience and 24/7 availability.",
            link: "eCommerce development",
            to:'/eCommerce-development'
        }
    ];

    const developmentServices = [
        {
            icon: <EnterpriseIcon />,
            title: "Enterprise software development",
            description: "We develop custom enterprise software solutions distinguished by three key attributes: a strong focus on business goals, adherence to best coding practices."
        },
        {
            icon: <MvpIcon />,
            title: "MVP development",
            description: "Over a decade, we helped multiple startups with our software application development services, offering fast, flexible, and reliable solutions."
        },
        {
            icon: <SaasIcon />,
            title: "SaaS development",
            description: "We build scalable and secure SaaS solutions with subscription management, multi-tenancy, and cloud-native architectures."
        },
        {
            icon: <ProductIcon />,
            title: "Product development",
            description: "From concept to market launch, we guide digital products through every stage of development."
        }
    ];

    const industries = [
        {
            icon: <HealthcareIcon />,
            title: "Healthcare",
            description: "Patient management systems, telemedicine platforms, IoT-powered applications, AI-powered diagnostics, data analytics and visualization.",
            link: "Healthcare software development"
        },
        {
            icon: <MediaIcon />,
            title: "Media & Entertainment",
            description: "We build streaming platforms, AI recommendations, social media tools, event systems, and cutting-edge digital media solutions."
        },
        {
            icon: <LogisticsIcon />,
            title: "Logistics",
            description: "Smart logistics solutions with real-time tracking, optimized routes, automated inventory, efficient fleet management, and seamless process automation.",
            link: "Logistics software development"
        },
        {
            icon: <FinanceIcon />,
            title: "Finances",
            description: "Wealth management software, compliance and reporting tools, payment processing solutions, insurance management systems.",
            link: "FinTech software development"
        },
        {
            icon: <EducationIcon />,
            title: "Education",
            description: "We deliver innovative EdTech solutions that transform and engage every step of the education process with custom digital learning tools.",
            link: "Education software development"
        },
        {
            icon: <RealEstateIcon />,
            title: "Real Estate",
            description: "Transform real estate with custom solutions for customer management, deal tracking, and optimized property offerings—driving efficiency and growth."
        },
        {
            icon: <EcommerceIcon />,
            title: "eCommerce",
            description: "Powerful eCommerce apps that deepen customer engagement with flawless shopping journeys across the web and mobile."
        },
        {
            icon: <MarketingIcon />,
            title: "Marketing",
            description: "Powerful marketing automation tools that optimize campaigns, gather actionable insights, and drive smarter marketing decisions—all in one platform.",
            link: "MarTech software development"
        }
    ];

    const qualityPractices = [
        {
            icon: <QualityIcon />,
            title: "Accurate cost estimation",
            description: "We conduct thorough business requirements analysis to provide precise budget and timeline assessments."
        },
        {
            icon: <TeamIcon />,
            title: "Competence-based team selection",
            description: "We assign only those specialists to each role who best fit the project needs in terms of their skills."
        },
        {
            icon: <RiskIcon />,
            title: "Comprehensive risk management",
            description: "We identify risks and develop a comprehensive mitigation plan for every project."
        },
        {
            icon: <AgileIcon />,
            title: "Agile delivery",
            description: "We work in iterations, enabling us to deliver new software components quickly."
        },
        {
            icon: <BudgetIcon />,
            title: "Budget control",
            description: "Our project manager monitors the budget execution on every iteration."
        },
        {
            icon: <DeadlineIcon />,
            title: "Responsible approach to deadlines",
            description: "With structured processes, we consistently meet deadlines without compromising quality."
        }
    ];

    const fadeIn = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0 }
    };

    const staggerContainer = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    return (
        <Box sx={{ bgcolor: 'background.default' }}>
            {/* Hero Section */}
            <Box
                sx={{
                    bgcolor: 'rgba(63, 81, 181, 0.9)',
                    color: 'primary.contrastText',
                    py: { xs: 8, md: 15 },
                    textAlign: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'url(/images/pattern.svg)',
                        opacity: 0.1,
                        pointerEvents: 'none'
                    }
                }}
            >
                <Container maxWidth="lg">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        <Stack spacing={4} alignItems="center">
                            <Chip
                                onClick={()=>console.log("")}
                                icon={<LanIcon />}
                                label="Next-Gen Services"
                                sx={{
                                    bgcolor: 'rgba(208, 227, 239, 0.55)',
                                    color: 'primary.contrastText',
                                    px: 2,
                                    py: 1,
                                    fontSize: '0.75rem'
                                }}
                            />

                            <Typography
                                variant={isMobile ? "h4" : "h2"}
                                component="h1"
                                sx={{
                                    fontWeight: 700,
                                    textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                    px: isMobile ? 2 : 0
                                }}
                            >
                                Our <Box component="span" sx={{ color: '#F0EAD6' }}>Services</Box>
                            </Typography>
                            <Typography
                                variant={isMobile ? "body1" : "h5"}
                                sx={{
                                    maxWidth: 800,
                                    opacity: 0.9,
                                    fontWeight: 300,
                                    px: isMobile ? 2 : 0,
                                    fontSize: isMobile ? '1rem' : 'inherit'
                                }}
                            >
                                For over a decade, we have been accumulating knowledge and expertise in several services that became our specialization.
                            </Typography>
                            <Button
                                variant="contained"
                                color="secondary"
                                size={isMobile ? "medium" : "large"}
                                onClick={()=>navigate('/get-in-touch')}
                                sx={{
                                    px: isMobile ? 4 : 6,
                                    py: 1.5,
                                    fontSize: isMobile ? '0.875rem' : '1rem',
                                    fontWeight: 600,
                                    borderRadius: '50px',
                                    boxShadow: `0 4px 20px ${alpha(theme.palette.secondary.main, 0.3)}`,
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: `0 6px 24px ${alpha(theme.palette.secondary.main, 0.4)}`
                                    },
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                Get in touch
                            </Button>
                        </Stack>
                    </motion.div>
                </Container>
            </Box>

            {/* Main Services Section */}
            <Container maxWidth="lg" sx={{ py: isMobile ? 6 : 12 }}>
                <Stack spacing={6}>
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <Stack spacing={2} textAlign="center" alignItems="center">
                            <Typography
                                variant="overline"
                                color="primary.main"
                                sx={{
                                    fontWeight: 600,
                                    letterSpacing: 1.5,
                                    fontSize: isMobile ? '0.65rem' : '0.75rem'
                                }}
                            >
                                WHAT WE DO
                            </Typography>
                            <Typography
                                variant={isMobile ? "h5" : "h3"}
                                component="h2"
                                sx={{
                                    fontWeight: 700,
                                    maxWidth: 600,
                                    px: isMobile ? 2 : 0
                                }}
                            >
                                Comprehensive digital solutions for your business
                            </Typography>
                            <Divider
                                sx={{
                                    width: 80,
                                    height: 4,
                                    bgcolor: 'primary.main',
                                    my: 2
                                }}
                            />
                        </Stack>
                    </motion.div>

                    <motion.div
                        variants={staggerContainer}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                    >
                        <Grid container spacing={isMobile ? 2 : 4}>
                            {mainServices.map((service, index) => (
                                <AnimatedGrid
                                    item
                                    xs={12}
                                    md={6}
                                    key={index}
                                    variants={fadeIn}
                                >
                                    <AnimatedCard
                                        elevation={0}
                                        sx={{
                                            height: '100%',
                                            p: isMobile ? 2 : 4,
                                            borderRadius: 2,
                                            border: `1px solid ${theme.palette.divider}`,
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                transform: 'translateY(-5px)',
                                                boxShadow: `0 16px 32px ${alpha(theme.palette.primary.main, 0.1)}`,
                                                borderColor: 'primary.main'
                                            }
                                        }}
                                        whileHover={{ y: -5 }}
                                    >
                                        <Stack direction="row" spacing={isMobile ? 2 : 3} alignItems="flex-start">
                                            <Box
                                                sx={{
                                                    width: isMobile ? 48 : 64,
                                                    height: isMobile ? 48 : 64,
                                                    bgcolor: 'primary.light',
                                                    color: 'primary.contrastText',
                                                    borderRadius: '12px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0,
                                                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                                                }}
                                            >
                                                {service.icon}
                                            </Box>
                                            <Box>
                                                <Typography
                                                    variant={isMobile ? "h6" : "h5"}
                                                    component="h3"
                                                    gutterBottom
                                                    sx={{ fontWeight: 600 }}
                                                >
                                                    {service.title}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ mb: 2 }}
                                                >
                                                    {service.description}
                                                </Typography>
                                                <Button
                                                    variant="text"
                                                    color="primary"
                                                    onClick={()=>navigate(`${service.to}`)}
                                                    endIcon={<span>&rarr;</span>}
                                                    size={isMobile ? "small" : "medium"}
                                                    sx={{
                                                        px: 0,
                                                        fontWeight: 600,
                                                        fontSize: isMobile ? '0.875rem' : '1rem',
                                                        '&:hover': {
                                                            backgroundColor: 'transparent',
                                                            color: 'primary.dark'
                                                        }
                                                    }}
                                                >
                                                    {service.link}
                                                </Button>
                                            </Box>
                                        </Stack>
                                    </AnimatedCard>
                                </AnimatedGrid>
                            ))}
                        </Grid>
                    </motion.div>
                </Stack>
            </Container>

            {/* Development Services Section */}
            <Box
                sx={{
                    py: isMobile ? 6 : 12,
                    background: `linear-gradient(180deg, ${theme.palette.background.default} 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
                    position: 'relative',
                    overflow: 'hidden',
                    textAlign: 'center'
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: -100,
                        right: -100,
                        width: 400,
                        height: 400,
                        borderRadius: '50%',
                        background: `radial-gradient(${alpha(theme.palette.primary.light, 0.2)} 0%, transparent 70%)`,
                        zIndex: 0
                    }}
                />
                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                    <Stack spacing={6}>
                        <motion.div
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            <Stack spacing={2} textAlign="center" alignItems="center">
                                <Typography
                                    variant="overline"
                                    color="primary.main"
                                    sx={{
                                        fontWeight: 600,
                                        letterSpacing: 1,
                                        fontSize: isMobile ? '0.65rem' : '0.75rem'
                                    }}
                                >
                                    DEVELOPMENT APPROACH
                                </Typography>
                                <Typography
                                    variant={isMobile ? "h5" : "h3"}
                                    component="h2"
                                    sx={{
                                        fontWeight: 700,
                                        maxWidth: 800,
                                        px: isMobile ? 2 : 0
                                    }}
                                >
                                    From MVPs to enterprise solutions: tailored development services
                                </Typography>
                                <Typography
                                    variant={isMobile ? "body1" : "h6"}
                                    color="text.secondary"
                                    sx={{
                                        maxWidth: 700,
                                        fontWeight: 300,
                                        px: isMobile ? 2 : 0,
                                        fontSize: isMobile ? '0.875rem' : 'inherit'
                                    }}
                                >
                                    We excel in developing software solutions for various business types and stages, combining product expertise with a commitment to scalability, reliability, and success.
                                </Typography>
                            </Stack>
                        </motion.div>

                        <motion.div
                            variants={staggerContainer}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                        >
                            <Grid
                                container
                                spacing={isMobile ? 2 : 4}
                                sx={{
                                    width: '100%',
                                    maxWidth: '800px',
                                    margin: '0 auto',
                                    px: isMobile ? 1 : 2
                                }}
                            >
                                {developmentServices.map((service, index) => (
                                    <AnimatedGrid
                                        item
                                        xs={12}
                                        sm={6}
                                        key={index}
                                        variants={fadeIn}
                                    >
                                        <AnimatedCard
                                            elevation={0}
                                            sx={{
                                                height: '100%',
                                                p: isMobile ? 2 : 4,
                                                bgcolor: 'background.paper',
                                                borderRadius: 2,
                                                border: `1px solid ${theme.palette.divider}`,
                                                transition: 'all 0.3s ease',
                                                '&:hover': {
                                                    transform: 'translateY(-5px)',
                                                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}`
                                                }
                                            }}
                                            whileHover={{ y: -5 }}
                                        >
                                            <Stack
                                                direction={{ xs: 'column', sm: 'row' }}
                                                spacing={isMobile ? 1 : 3}
                                                alignItems={{ xs: 'center', sm: 'flex-start' }}
                                                textAlign={{ xs: 'center', sm: 'left' }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: isMobile ? 40 : 48,
                                                        height: isMobile ? 40 : 48,
                                                        bgcolor: 'primary.main',
                                                        color: 'primary.contrastText',
                                                        borderRadius: '12px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        flexShrink: 0,
                                                        boxShadow: `0 4px 8px ${alpha(theme.palette.primary.main, 0.3)}`
                                                    }}
                                                >
                                                    {service.icon}
                                                </Box>
                                                <Box>
                                                    <Typography
                                                        variant={isMobile ? "subtitle1" : "h5"}
                                                        component="h3"
                                                        gutterBottom
                                                        sx={{ fontWeight: 600 }}
                                                    >
                                                        {service.title}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                    >
                                                        {service.description}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </AnimatedCard>
                                    </AnimatedGrid>
                                ))}
                            </Grid>
                        </motion.div>
                    </Stack>
                </Container>
            </Box>

            {/* Industries Section */}
            <Container maxWidth="lg" sx={{ py: isMobile ? 6 : 12 }}>
                <Stack spacing={6}>
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <Stack spacing={2} textAlign="center" alignItems="center">
                            <Typography
                                variant="overline"
                                color="primary.main"
                                sx={{
                                    fontWeight: 600,
                                    letterSpacing: 1.5,
                                    fontSize: isMobile ? '0.65rem' : '0.75rem'
                                }}
                            >
                                INDUSTRY EXPERTISE
                            </Typography>
                            <Typography
                                variant={isMobile ? "h5" : "h3"}
                                component="h2"
                                sx={{
                                    fontWeight: 700,
                                    maxWidth: 600,
                                    px: isMobile ? 2 : 0
                                }}
                            >
                                Development services across multiple industries
                            </Typography>

                            <Typography
                                variant={isMobile ? "body1" : "h6"}
                                color="text.secondary"
                                sx={{
                                    fontWeight: 300,
                                    px: isMobile ? 2 : 0,
                                    fontSize: isMobile ? '0.875rem' : 'inherit'
                                }}
                            >
                                We've built expert knowledge in a range of industries.
                            </Typography>

                            <Divider
                                sx={{
                                    width: 80,
                                    height: 4,
                                    bgcolor: 'primary.main',
                                    my: 2
                                }}
                            />
                        </Stack>
                    </motion.div>

                    <motion.div
                        variants={staggerContainer}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                    >
                        <Grid container spacing={isMobile ? 2 : 4} sx={{ maxWidth: '70rem', px: isMobile ? 1 : 0 }}>
                            {industries.map((service, index) => (
                                <AnimatedGrid
                                    item
                                    xs={12}
                                    md={6}
                                    key={index}
                                    variants={fadeIn}
                                >
                                    <AnimatedCard
                                        elevation={0}
                                        sx={{
                                            height: '100%',
                                            p: isMobile ? 2 : 4,
                                            borderRadius: 2,
                                            border: `1px solid ${theme.palette.divider}`,
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                transform: 'translateY(-5px)',
                                                boxShadow: `0 16px 32px ${alpha(theme.palette.primary.main, 0.1)}`,
                                                borderColor: 'primary.main'
                                            }
                                        }}
                                        whileHover={{ y: -5 }}
                                    >
                                        <Stack direction="row" spacing={isMobile ? 2 : 3} alignItems="flex-start">
                                            <Box
                                                sx={{
                                                    width: isMobile ? 48 : 64,
                                                    height: isMobile ? 48 : 64,
                                                    bgcolor: 'primary.light',
                                                    color: 'primary.contrastText',
                                                    borderRadius: '12px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0,
                                                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                                                }}
                                            >
                                                {service.icon}
                                            </Box>
                                            <Box>
                                                <Typography
                                                    variant={isMobile ? "subtitle1" : "h5"}
                                                    component="h3"
                                                    gutterBottom
                                                    sx={{ fontWeight: 600 }}
                                                >
                                                    {service.title}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ mb: 2 }}
                                                >
                                                    {service.description}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </AnimatedCard>
                                </AnimatedGrid>
                            ))}
                        </Grid>
                    </motion.div>
                </Stack>
            </Container>

            {/* Quality Practices Section */}
            <Box
                sx={{
                    py: isMobile ? 6 : 12,
                    background: `linear-gradient(180deg, ${theme.palette.background.default} 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
                    position: 'relative',
                    overflow: 'hidden',
                    textAlign: 'center'
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: -100,
                        right: -100,
                        width: 400,
                        height: 400,
                        borderRadius: '50%',
                        background: `radial-gradient(${alpha(theme.palette.primary.light, 0.2)} 0%, transparent 70%)`,
                        zIndex: 0
                    }}
                />
                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                    <Stack spacing={6}>
                        <motion.div
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            <Stack spacing={2} textAlign="center" alignItems="center">
                                <Typography
                                    variant="overline"
                                    color="primary.main"
                                    sx={{
                                        fontWeight: 600,
                                        letterSpacing: 1,
                                        fontSize: isMobile ? '0.65rem' : '0.75rem'
                                    }}
                                >
                                    OUR PROCESS
                                </Typography>
                                <Typography
                                    variant={isMobile ? "h5" : "h3"}
                                    component="h2"
                                    sx={{
                                        fontWeight: 700,
                                        maxWidth: 800,
                                        px: isMobile ? 2 : 0
                                    }}
                                >
                                    Quality control practices
                                </Typography>
                                <Typography
                                    variant={isMobile ? "body1" : "h6"}
                                    color="text.secondary"
                                    sx={{
                                        maxWidth: 700,
                                        fontWeight: 300,
                                        px: isMobile ? 2 : 0,
                                        fontSize: isMobile ? '0.875rem' : 'inherit'
                                    }}
                                >
                                    We have established and implemented internal guidelines that set the standards for the quality of our work.
                                </Typography>
                            </Stack>
                        </motion.div>

                        <motion.div
                            variants={staggerContainer}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                        >
                            <Grid
                                container
                                spacing={isMobile ? 2 : 4}
                                sx={{
                                    width: '100%',
                                    maxWidth: '800px',
                                    margin: '0 auto',
                                    px: isMobile ? 1 : 2
                                }}
                            >
                                {qualityPractices.map((service, index) => (
                                    <AnimatedGrid
                                        item
                                        xs={12}
                                        sm={6}
                                        key={index}
                                        variants={fadeIn}
                                    >
                                        <AnimatedCard
                                            elevation={0}
                                            sx={{
                                                height: '100%',
                                                p: isMobile ? 2 : 4,
                                                bgcolor: 'background.paper',
                                                borderRadius: 2,
                                                border: `1px solid ${theme.palette.divider}`,
                                                transition: 'all 0.3s ease',
                                                '&:hover': {
                                                    transform: 'translateY(-5px)',
                                                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}`
                                                }
                                            }}
                                            whileHover={{ y: -5 }}
                                        >
                                            <Stack
                                                direction={{ xs: 'column', sm: 'row' }}
                                                spacing={isMobile ? 1 : 3}
                                                alignItems={{ xs: 'center', sm: 'flex-start' }}
                                                textAlign={{ xs: 'center', sm: 'left' }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: isMobile ? 40 : 48,
                                                        height: isMobile ? 40 : 48,
                                                        bgcolor: 'primary.main',
                                                        color: 'primary.contrastText',
                                                        borderRadius: '12px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        flexShrink: 0,
                                                        boxShadow: `0 4px 8px ${alpha(theme.palette.primary.main, 0.3)}`
                                                    }}
                                                >
                                                    {service.icon}
                                                </Box>
                                                <Box>
                                                    <Typography
                                                        variant={isMobile ? "subtitle1" : "h5"}
                                                        component="h3"
                                                        gutterBottom
                                                        sx={{ fontWeight: 600 }}
                                                    >
                                                        {service.title}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                    >
                                                        {service.description}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </AnimatedCard>
                                    </AnimatedGrid>
                                ))}
                            </Grid>
                        </motion.div>
                    </Stack>
                </Container>
            </Box>

            {/* CTA Section */}
            <Box
                sx={{
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                    color: 'primary.contrastText',
                    py: isMobile ? 8 : 12,
                    textAlign: 'center',
                    position: 'relative',
                    overflow: 'hidden'
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'url(/images/pattern.svg)',
                        opacity: 0.1,
                        pointerEvents: 'none'
                    }}
                />
                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                    >
                        <Stack spacing={4} alignItems="center">
                            <Typography
                                variant={isMobile ? "h5" : "h3"}
                                component="h2"
                                sx={{
                                    fontWeight: 700,
                                    textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                    px: isMobile ? 2 : 0
                                }}
                            >
                                Ready to discuss your project?
                            </Typography>
                            <Typography
                                variant={isMobile ? "body1" : "h6"}
                                sx={{
                                    maxWidth: 700,
                                    opacity: 0.9,
                                    fontWeight: 300,
                                    px: isMobile ? 2 : 0,
                                    fontSize: isMobile ? '0.875rem' : 'inherit'
                                }}
                            >
                                Whether you need one specialist or a complete development team, we have the right talent for your project.
                            </Typography>
                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={()=>navigate('/get-in-touch')}
                                size={isMobile ? "medium" : "large"}
                                sx={{
                                    px: isMobile ? 4 : 6,
                                    py: 1.5,
                                    fontSize: isMobile ? '0.875rem' : '1rem',
                                    fontWeight: 600,
                                    borderRadius: '50px',
                                    boxShadow: `0 4px 20px ${alpha(theme.palette.secondary.main, 0.3)}`,
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: `0 6px 24px ${alpha(theme.palette.secondary.main, 0.4)}`
                                    },
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                Get in touch
                            </Button>
                        </Stack>
                    </motion.div>
                </Container>
            </Box>
        </Box>
    );
};

export default ServicesPage;